import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Link2,
  Flag,
  VolumeX,
  Ban,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DeletePostDialog } from '@/components/DeletePostDialog'
import { EditPostModal } from '@/components/EditPostModal'
import { ReportModal } from '@/components/ReportModal'
import { FeedItem, socialApi } from '@/lib/api'
import { toast } from '@/components/ui/use-toast'

interface PostMenuProps {
  item: FeedItem
  isOwnPost: boolean
  onDelete?: (postId: string) => void
  onEdit?: (postId: string, content: string, mediaIds: string[]) => void
}

export function PostMenu({ item, isOwnPost, onDelete, onEdit }: PostMenuProps) {
  const queryClient = useQueryClient()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)

  const post = item.post.post
  const author = item.post.author

  const muteMutation = useMutation({
    mutationFn: (userId: string) => socialApi.muteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      toast({
        title: 'User muted',
        description: `You won't see posts from @${author?.username} anymore`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to mute user',
        description: error.message || 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  const blockMutation = useMutation({
    mutationFn: (userId: string) => socialApi.blockUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      toast({
        title: 'User blocked',
        description: `@${author?.username} has been blocked`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to block user',
        description: error.message || 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  const handleCopyLink = async () => {
    const postUrl = `${window.location.origin}/posts/${post.id}`
    try {
      await navigator.clipboard.writeText(postUrl)
      toast({
        title: 'Link copied',
        description: 'Post link copied to clipboard',
      })
    } catch (err) {
      toast({
        title: 'Failed to copy',
        description: 'Could not copy link to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleMute = () => {
    if (author?.id) {
      muteMutation.mutate(author.id)
    }
  }

  const handleBlock = () => {
    if (author?.id) {
      blockMutation.mutate(author.id)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {isOwnPost ? (
            <>
              <DropdownMenuItem onClick={() => setShowEditModal(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit post
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-600 focus:text-red-600 dark:text-red-400"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete post
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCopyLink}>
                <Link2 className="mr-2 h-4 w-4" />
                Copy link
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem onClick={() => setShowReportModal(true)}>
                <Flag className="mr-2 h-4 w-4" />
                Report post
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleMute}>
                <VolumeX className="mr-2 h-4 w-4" />
                Mute @{author?.username}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleBlock}
                className="text-red-600 focus:text-red-600 dark:text-red-400"
              >
                <Ban className="mr-2 h-4 w-4" />
                Block @{author?.username}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCopyLink}>
                <Link2 className="mr-2 h-4 w-4" />
                Copy link
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <DeletePostDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        postId={post.id}
        onDelete={onDelete}
      />

      {/* Edit Post Modal */}
      <EditPostModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        item={item}
        onEdit={onEdit}
      />

      {/* Report Modal */}
      <ReportModal
        open={showReportModal}
        onOpenChange={setShowReportModal}
        contentType="post"
        contentId={post.id}
        authorUsername={author?.username || 'user'}
      />
    </>
  )
}
